import {
  useFormState,
  useStateObject,
  isObjectEmptyData,
} from 'holistic-resources';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import DefaultActionButtons from 'components/DefaultActionButtons';
import Modal from 'components/Modal';
import TextField from 'components/formFields/TextField';
import {
  showSuccessFeedbackBar,
  showErrorFeedbackBar,
} from 'reducers/ui/uiSlice';
import { updateVisitor } from 'services/ApiService';
import {
  UPDATE_SUCCESS,
  UPDATE_ERROR,
} from 'shared/constants/feedbackMessages';
import { validateFormRequiriedFields } from 'shared/helpers/common';

const getAddressList = (
  addressList = [],
  primaryIndex = -1,
  newAddress = null,
) => {
  const list = addressList.map((item, index) => ({
    ...item,
    primary: primaryIndex === index,
  }));

  if (newAddress) {
    list.push(newAddress);
  }

  return [...list];
};

export default function AddressTabEditModal({
  isOpen,
  setModalState,
  primaryAddressIndex,
  setUserData,
  userData = {},
  moduleConfig = {},
}) {
  const [fieldsConfig, setFieldsConfig] = useState({});
  const dispatch = useDispatch();
  const {
    formValue: address,
    setFormValue: setAddress,
    formChangeHandler: onChangeHandler,
  } = useFormState({});
  const { value: formErrorMessages, updateValue: setFormErrorMessages } =
    useStateObject({});

  useEffect(() => {
    setFieldsConfig({
      address1: {
        required: moduleConfig?.['Client-Client-view-address1-mandatory'],
        visible: moduleConfig?.['Client-Client-view-address1-visible'],
        value: address?.address1,
        fieldName: 'address1',
        label: 'Address line 1',
      },
      address2: {
        required: moduleConfig?.['Client-Client-view-address2-mandatory'],
        visible: moduleConfig?.['Client-Client-view-address2-visible'],
        value: address?.address2,
        fieldName: 'address2',
        label: 'Address line 2',
      },
      address3: {
        required: moduleConfig?.['Client-Client-view-address3-mandatory'],
        visible: moduleConfig?.['Client-Client-view-address3-visible'],
        value: address?.address3,
        fieldName: 'address3',
        label: 'Address line 3',
      },
      city: {
        required: moduleConfig?.['Client-Client-view-city-mandatory'],
        visible: moduleConfig?.['Client-Client-view-city-visible'],
        value: address?.city,
        fieldName: 'city',
        label: 'Town / City',
      },
      countyProvince: {
        required: moduleConfig?.['Client-Client-view-countyProvince-mandatory'],
        visible: moduleConfig?.['Client-Client-view-countyProvince-visible'],
        value: address?.countyProvince,
        fieldName: 'countyProvince',
        label: 'County',
      },
      zipOrPostcode: {
        required: moduleConfig?.['Client-Client-view-zipOrPostcode-mandatory'],
        visible: moduleConfig?.['Client-Client-view-zipOrPostcode-visible'],
        value: address?.zipOrPostcode,
        fieldName: 'zipOrPostcode',
        label: 'Postcode',
      },
      country: {
        required: moduleConfig?.['Client-Client-view-country-mandatory'],
        visible: moduleConfig?.['Client-Client-view-country-visible'],
        value: address?.country,
        fieldName: 'country',
        label: 'Country',
      },
    });
  }, [address, moduleConfig]);

  useEffect(() => {
    if (primaryAddressIndex === -1) {
      setAddress({
        primary: true,
        active: true,
        dateCreated: new Date().getTime(),
        type: 'OTHER',
      });

      return;
    }

    if (userData?.addresses?.[primaryAddressIndex]) {
      setAddress(userData?.addresses?.[primaryAddressIndex]);
    }
  }, [userData, primaryAddressIndex, setAddress]);

  const closeModal = () => {
    setModalState(false);
  };

  const getUpdatedAddresses = () => {
    const { addresses: currentAddresses = [] } = userData;

    if (primaryAddressIndex === -1) {
      return getAddressList([...currentAddresses], primaryAddressIndex, {
        ...address,
      });
    }

    currentAddresses[primaryAddressIndex] = { ...address };
    return getAddressList([...currentAddresses], primaryAddressIndex);
  };

  const onSave = async () => {
    const isFormValid = validateFormRequiriedFields(
      setFormErrorMessages,
      fieldsConfig,
    );

    if (!isFormValid) {
      return;
    }

    const updatedAddresses = getUpdatedAddresses();

    try {
      const { data } = await updateVisitor({
        addresses: [...updatedAddresses],
        _id: userData?._id, // eslint-disable-line no-underscore-dangle
      });
      setUserData?.(data);
      closeModal();
      dispatch(showSuccessFeedbackBar(UPDATE_SUCCESS));
    } catch (error) {
      dispatch(showErrorFeedbackBar(UPDATE_ERROR));
      console.error(error);
    }
  };

  const onCancel = () => {
    closeModal();
  };

  const {
    address1,
    address2,
    address3,
    city,
    countyProvince,
    zipOrPostcode,
    country,
  } = address;
  const fieldStyle = 'basis-[48%] my-2';

  return (
    <Modal
      show={isOpen}
      onClose={() => setModalState(false)}
      header="Edit Address"
      body={
        <div className="flex flex-wrap gap-x-4 w-full">
          <TextField
            isVisible={fieldsConfig?.address1?.visible}
            isRequired={fieldsConfig?.address1?.required}
            label={fieldsConfig?.address1?.label}
            name={fieldsConfig?.address1?.fieldName}
            setFormErrorMessages={setFormErrorMessages}
            errorMessage={formErrorMessages?.address1}
            value={address1}
            onChange={onChangeHandler}
            containerClassName={fieldStyle}
          />
          <TextField
            isVisible={fieldsConfig?.address2?.visible}
            isRequired={fieldsConfig?.address2?.required}
            label={fieldsConfig?.address2?.label}
            name={fieldsConfig?.address2?.fieldName}
            setFormErrorMessages={setFormErrorMessages}
            errorMessage={formErrorMessages?.address2}
            value={address2}
            onChange={onChangeHandler}
            containerClassName={fieldStyle}
          />
          <TextField
            isVisible={fieldsConfig?.address3?.visible}
            isRequired={fieldsConfig?.address3?.required}
            label={fieldsConfig?.address3?.label}
            name={fieldsConfig?.address3?.fieldName}
            setFormErrorMessages={setFormErrorMessages}
            errorMessage={formErrorMessages?.address3}
            value={address3}
            onChange={onChangeHandler}
            containerClassName={fieldStyle}
          />
          <TextField
            isVisible={fieldsConfig?.city?.visible}
            isRequired={fieldsConfig?.city?.required}
            label={fieldsConfig?.city?.label}
            name={fieldsConfig?.city?.fieldName}
            setFormErrorMessages={setFormErrorMessages}
            errorMessage={formErrorMessages?.city}
            value={city}
            onChange={onChangeHandler}
            containerClassName={fieldStyle}
          />
          <TextField
            isVisible={fieldsConfig?.countyProvince?.visible}
            isRequired={fieldsConfig?.countyProvince?.required}
            label={fieldsConfig?.countyProvince?.label}
            name={fieldsConfig?.countyProvince?.fieldName}
            setFormErrorMessages={setFormErrorMessages}
            errorMessage={formErrorMessages?.countyProvince}
            value={countyProvince}
            onChange={onChangeHandler}
            containerClassName={fieldStyle}
          />
          <TextField
            isVisible={fieldsConfig?.zipOrPostcode?.visible}
            isRequired={fieldsConfig?.zipOrPostcode?.required}
            label={fieldsConfig?.zipOrPostcode?.label}
            name={fieldsConfig?.zipOrPostcode?.fieldName}
            setFormErrorMessages={setFormErrorMessages}
            errorMessage={formErrorMessages?.zipOrPostcode}
            value={zipOrPostcode}
            onChange={onChangeHandler}
            containerClassName={fieldStyle}
          />
          <TextField
            isVisible={fieldsConfig?.country?.visible}
            isRequired={fieldsConfig?.country?.required}
            label={fieldsConfig?.country?.label}
            name={fieldsConfig?.country?.fieldName}
            setFormErrorMessages={setFormErrorMessages}
            errorMessage={formErrorMessages?.country}
            value={country}
            onChange={onChangeHandler}
            containerClassName={fieldStyle}
          />
        </div>
      }
      footer={
        <DefaultActionButtons
          onSecondaryButtonClick={onCancel}
          onPrimaryButtonClick={onSave}
          isPrimaryButtonDisabled={!isObjectEmptyData(formErrorMessages)}
        />
      }
    />
  );
}
